import { makeStyles, Theme } from '@material-ui/core/styles'
/**
 * Styles for RentRollRoot component
 */
type RentRollRootStylesProps = {
    headerHeight: number
    collapsableSelectorHeight: number
}
export const useRentRollRootStyles = makeStyles<Theme, RentRollRootStylesProps>(
    (theme) => ({
        container: {
            display: 'flex',
            flexDirection: 'column',
            height: ({ headerHeight, collapsableSelectorHeight }) =>
                `calc(100vh - ${headerHeight + collapsableSelectorHeight}px)`,
            padding: theme.spacing(2),
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(2),
            flexWrap: 'wrap',
            gap: theme.spacing(2),
        },
        toolbarLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
        },
        toolbarRight: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        uploadSection: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(2),
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.spacing(1),
            marginBottom: theme.spacing(2),
        },
        dropzone: {
            border: `2px dashed ${theme.palette.grey[300]}`,
            borderRadius: theme.spacing(1),
            padding: theme.spacing(2),
            textAlign: 'center',
            cursor: 'pointer',
            flex: 1,
            transition: 'all 0.2s ease',
            '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light + '10',
            },
        },
        dropzoneActive: {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + '20',
        },
        tableContainer: {
            flex: 1,
            overflow: 'auto',
        },
        tableHeader: {
            position: 'sticky',
            top: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: 1,
        },
        headerCell: {
            fontWeight: 700,
            backgroundColor: theme.palette.grey[100],
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': {
                backgroundColor: theme.palette.grey[200],
            },
        },
        cell: {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
        },
        timePeriodChip: {
            fontSize: '0.7rem',
            height: 20,
        },
        pastChip: {
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.grey[700],
        },
        currentChip: {
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.dark,
        },
        futureChip: {
            backgroundColor: theme.palette.info.light,
            color: theme.palette.info.dark,
        },
        pagination: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(2),
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        drawer: {
            width: 360,
            padding: theme.spacing(3),
        },
        drawerHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(3),
        },
        attributeItem: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(1),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(1),
        },
        emptyState: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: theme.spacing(2, 2, 4),
            color: theme.palette.text.secondary,
        },
        reviewPanel: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            maxWidth: 720,
            margin: '0 auto',
            paddingTop: theme.spacing(1),
        },
        emptyStateCard: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(4),
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: theme.spacing(2),
            backgroundColor: theme.palette.background.paper,
            maxWidth: 520,
            width: '100%',
        },
        emptyStateIcon: {
            fontSize: 44,
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + '20',
            borderRadius: '50%',
            padding: theme.spacing(1.5),
        },
        loadingState: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(8),
        },
        statsContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        statTile: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.5, 1),
            borderRadius: theme.spacing(0.5),
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
        },
        statLabel: {
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            fontWeight: 500,
        },
        statValue: {
            fontSize: '0.875rem',
            fontWeight: 700,
        },
        statPast: {
            color: theme.palette.grey[600],
        },
        statCurrent: {
            color: theme.palette.success.main,
        },
        statFuture: {
            color: theme.palette.info.main,
        },
        reviewCard: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            padding: theme.spacing(3),
            width: '100%',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(1.5),
            backgroundColor: theme.palette.background.paper,
        },
        reviewStats: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(2),
            width: '100%',
            padding: theme.spacing(1, 0),
        },
        reviewStatItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 160,
            padding: theme.spacing(1.5, 2),
            borderRadius: theme.spacing(1),
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
        },
        reviewStatValue: {
            fontWeight: 700,
            fontSize: '1.25rem',
        },
        reviewStatLabel: {
            fontSize: '0.8125rem',
            color: theme.palette.text.secondary,
        },
        reviewActions: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(1.5),
            width: '100%',
            marginTop: theme.spacing(0.5),
        },
        reviewActionButton: {
            minWidth: 200,
        },
        reviewSecondaryActions: {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginTop: theme.spacing(0.5),
        },
        reviewIconSuccess: {
            fontSize: 40,
            color: theme.palette.success.main,
        },
        reviewIconError: {
            fontSize: 40,
            color: theme.palette.error.main,
        },
    }),
)
